if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cartNotification = document.querySelector('cart-notification');
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.variantIdInput.addEventListener('change', this.onVariantIdChange.bind(this));
        this.statusMessage = this.querySelector('[data-product-form-status]');
        this.statusMessageTimeout = null;
        this.soldOutSignup = this.querySelector('[data-sold-out-signup]');
        this.soldOutSignupForm = this.soldOutSignup?.querySelector('.sold-out-signup__form');
        this.soldOutSignupInput = this.soldOutSignup?.querySelector('.sold-out-signup__input');
        this.soldOutSignupButton = this.soldOutSignup?.querySelector('.sold-out-signup__button');
        this.soldOutSignupButtonLabel = this.soldOutSignupButton?.querySelector('span');
        this.soldOutSignupSuccess = this.soldOutSignup?.querySelector('[data-sold-out-signup-success]');
        this.soldOutSignupControls = this.soldOutSignup?.querySelector('.sold-out-signup__controls');
        this.soldOutSignupSuccessTimeout = null;
        this.soldOutSignupSuccessExitTimeout = null;
        this.soldOutSignupEmbed = this.soldOutSignup?.querySelector('.sold-out-signup__embed');
        this.soldOutSignupEmbedObserver = null;

        if (this.cartDrawer) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setupSoldOutSignup();
        this.updateSoldOutSignupVisibility();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.setStatusMessage('');

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const sectionsToRender = new Set(['cart-icon-bubble', 'cart-live-region-text']);
        if (this.cartDrawer) {
          sectionsToRender.add('cart-drawer');
          formData.append('sections_url', window.location.pathname);
          this.cartDrawer.setActiveElement(document.activeElement);
        }
        sectionsToRender.forEach((section) => formData.append('sections', section));
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.setStatusMessage('');

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.renderCartResponse(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.renderCartResponse(response);
              });
            }
          })
          .catch((e) => {
            this.setStatusMessage('');
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cartDrawer && this.cartDrawer.classList.contains('is-empty')) this.cartDrawer.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      onVariantIdChange() {
        const quantityInput = this.form.querySelector('[data-upsell-quantity]');
        const selectedOption = this.variantIdInput.selectedOptions?.[0];

        if (quantityInput && selectedOption?.dataset.quantityMin) {
          quantityInput.value = selectedOption.dataset.quantityMin;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      renderCartResponse(response) {
        this.updateSectionFromMarkup('cart-icon-bubble', response.sections?.['cart-icon-bubble']);
        this.updateSectionFromMarkup('cart-live-region-text', response.sections?.['cart-live-region-text']);
        this.updateFloatingCartCountFromResponse(response);
        this.syncFloatingCartCount();
        this.setStatusMessage(window.cartStrings.itemAdded || window.cartStrings.itemUpdated, true);
        this.renderCartDrawer(response);
      }

      renderCartDrawer(response) {
        const drawerMarkup = response.sections?.['cart-drawer'];
        if (!this.cartDrawer || !drawerMarkup) return;

        this.cartDrawer.renderContents(response);
      }

      updateSectionFromMarkup(targetId, markup, selector = '.shopify-section') {
        const target = document.getElementById(targetId);
        if (!target || !markup) return;

        const sourceDocument = new DOMParser().parseFromString(markup, 'text/html');
        const sourceElement = sourceDocument.querySelector(selector);
        if (sourceElement) {
          target.innerHTML = sourceElement.innerHTML;
        }
      }

      updateFloatingCartCount(itemCount) {
        if (typeof itemCount !== 'number') return;
        document.querySelectorAll('.omnee-floating-cart__count').forEach((countElement) => {
          countElement.textContent = itemCount;
        });
      }

      updateFloatingCartCountFromResponse(response) {
        if (typeof response?.item_count === 'number') {
          this.updateFloatingCartCount(response.item_count);
          return;
        }

        const bubbleMarkup = response?.sections?.['cart-icon-bubble'];
        if (!bubbleMarkup) return;

        const bubbleDocument = new DOMParser().parseFromString(bubbleMarkup, 'text/html');
        const countText =
          bubbleDocument.querySelector('.cart-count-bubble [aria-hidden="true"]')?.textContent?.trim() || '';
        const parsedCount = Number.parseInt(countText, 10);

        if (!Number.isNaN(parsedCount)) {
          this.updateFloatingCartCount(parsedCount);
        }
      }

      syncFloatingCartCount() {
        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cartState) => {
            if (typeof cartState?.item_count === 'number') {
              this.updateFloatingCartCount(cartState.item_count);
            }
          })
          .catch((error) => {
            console.error('Unable to sync floating cart count after add to cart.', error);
          });
      }

      setStatusMessage(message, autoDismiss = false) {
        if (!this.statusMessage) return;

        window.clearTimeout(this.statusMessageTimeout);

        if (!message) {
          this.statusMessage.textContent = '';
          this.statusMessage.hidden = true;
          this.statusMessage.classList.remove('is-visible');
          return;
        }

        this.statusMessage.hidden = false;
        this.statusMessage.textContent = message;

        requestAnimationFrame(() => {
          this.statusMessage.classList.add('is-visible');
        });

        if (!autoDismiss) return;

        this.statusMessageTimeout = window.setTimeout(() => {
          this.statusMessage.classList.remove('is-visible');
          window.setTimeout(() => {
            if (!this.statusMessage.classList.contains('is-visible')) {
              this.statusMessage.textContent = '';
              this.statusMessage.hidden = true;
            }
          }, 220);
        }, 3000);
      }

      toggleSubmitButton(disable = true, text, availableText) {
        if (disable && !text) this.querySelector('restock-signup')?.suspend();
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = availableText || window.variantStrings.addToCart;
        }

        this.updateSoldOutSignupVisibility(disable && !text ? '' : text);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      setupSoldOutSignup() {
        if (!this.soldOutSignup) return;

        this.soldOutSignupTrigger = this.soldOutSignup.querySelector('[data-sold-out-signup-trigger]');
        this.soldOutSignupPanel = this.soldOutSignup.querySelector('[data-sold-out-signup-panel]');
        this.soldOutSignupPanelTimeout = null;
        this.soldOutSignupInitiallyOpen = this.soldOutSignupPanel?.dataset.initiallyOpen === 'true';

        this.soldOutSignupTrigger?.addEventListener('click', () => {
          const shouldOpen = !this.soldOutSignupPanel?.classList.contains('is-open');
          this.setSoldOutSignupPanelOpen(shouldOpen);
        });

        ['mouseenter', 'focus'].forEach((eventName) => {
          this.soldOutSignupButton?.addEventListener(eventName, () => this.updateSoldOutSignupButtonText(true));
        });

        ['mouseleave', 'blur'].forEach((eventName) => {
          this.soldOutSignupButton?.addEventListener(eventName, () => this.updateSoldOutSignupButtonText(false));
        });

        if (this.soldOutSignupInitiallyOpen) {
          this.soldOutSignupPanel.hidden = false;
          this.soldOutSignupPanel.classList.add('is-open');
          this.soldOutSignupTrigger?.setAttribute('aria-expanded', 'true');
        }

        this.initializeSoldOutSignupEmbedAccessibility();
      }

      initializeSoldOutSignupEmbedAccessibility() {
        if (!this.soldOutSignupEmbed) return;

        this.applySoldOutSignupEmbedTitles();

        this.soldOutSignupEmbedObserver?.disconnect();
        this.soldOutSignupEmbedObserver = new MutationObserver(() => this.applySoldOutSignupEmbedTitles());
        this.soldOutSignupEmbedObserver.observe(this.soldOutSignupEmbed, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['title'],
        });
      }

      applySoldOutSignupEmbedTitles() {
        if (!this.soldOutSignupEmbed) return;

        const productTitle =
          this.closest('product-info')?.querySelector('.product__title h1, .product__title .h1')?.textContent?.trim() ||
          document.title.split('–')[0].trim() ||
          'Product';
        const iframeTitle = `${productTitle} email signup`;

        this.soldOutSignupEmbed.querySelectorAll('iframe:not([title]), iframe[title=""]').forEach((iframe) => {
          iframe.setAttribute('title', iframeTitle);
        });
      }

      updateSoldOutSignupButtonText(useHoverText) {
        if (!this.soldOutSignupButton || !this.soldOutSignupButtonLabel) return;

        const nextText = useHoverText
          ? this.soldOutSignupButton.dataset.hoverText
          : this.soldOutSignupButton.dataset.defaultText;

        if (nextText) this.soldOutSignupButtonLabel.textContent = nextText;
      }

      updateSoldOutSignupVisibility(text = this.submitButtonText?.textContent) {
        if (!this.soldOutSignup || !this.submitButton) return;

        const triggerLabels = (this.dataset.notifyTriggerLabels || '')
          .split('||')
          .map((label) => label.trim())
          .filter(Boolean);
        const buttonText = (text || this.submitButtonText?.textContent || '').trim();
        const isDisabled = this.submitButton.hasAttribute('disabled');
        const shouldShow = isDisabled && triggerLabels.includes(buttonText);

        this.soldOutSignup.hidden = !shouldShow;

        if (!shouldShow) {
          this.resetSoldOutSignupState();
          this.setSoldOutSignupPanelOpen(false, true);
        } else {
          this.soldOutSignupTrigger?.setAttribute(
            'aria-expanded',
            this.soldOutSignupPanel?.classList.contains('is-open') ? 'true' : 'false'
          );
        }
      }

      resetSoldOutSignupState() {
        window.clearTimeout(this.soldOutSignupSuccessTimeout);
        window.clearTimeout(this.soldOutSignupSuccessExitTimeout);
        window.clearTimeout(this.soldOutSignupPanelTimeout);
        this.soldOutSignupInput?.setCustomValidity('');
        this.soldOutSignupForm?.reset();
        this.soldOutSignupControls?.removeAttribute('hidden');
        if (this.soldOutSignupSuccess) {
          this.soldOutSignupSuccess.hidden = true;
          this.soldOutSignupSuccess.classList.remove('is-visible', 'is-exiting');
        }
        this.updateSoldOutSignupButtonText(false);
      }

      setSoldOutSignupPanelOpen(isOpen, immediate = false) {
        if (!this.soldOutSignupPanel) return;

        window.clearTimeout(this.soldOutSignupPanelTimeout);
        this.soldOutSignupTrigger?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        if (isOpen) {
          this.soldOutSignupPanel.hidden = false;
          requestAnimationFrame(() => {
            this.soldOutSignupPanel.classList.add('is-open');
          });
          return;
        }

        this.soldOutSignupPanel.classList.remove('is-open');
        if (immediate) {
          this.soldOutSignupPanel.hidden = true;
          return;
        }

        this.soldOutSignupPanelTimeout = window.setTimeout(() => {
          this.soldOutSignupPanel.hidden = true;
        }, 220);
      }
    }
  );
}

if (!customElements.get('restock-signup')) {
  customElements.define('restock-signup', class RestockSignup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.observeBuyButton();
        return;
      }
      this.initialized = true;
      this.trigger = this.querySelector('[data-restock-trigger]');
      this.panel = this.querySelector('[data-restock-panel]');
      this.form = this.querySelector('[data-restock-form]');
      this.status = this.querySelector('[data-restock-status]');
      this.submit = this.form.querySelector('[type="submit"]');
      this.smsForm = this.querySelector('[data-restock-sms-form]');
      this.smsSubmit = this.smsForm.querySelector('[type="submit"]');
      this.smsSkip = this.querySelector('[data-restock-sms-skip]');
      this.support = this.querySelector('[data-restock-support]');
      this.phoneCountry = this.querySelector('[data-restock-phone-country]');
      this.setupPhoneCountries();
      this.observeBuyButton();
      this.flowVersion = 0;
      this.step = 'email';
      this.smsForm.addEventListener('submit', (event) => this.subscribeSms(event));
      this.smsForm.elements.phone.addEventListener('input', () => this.smsForm.elements.phone.setCustomValidity(''));
      this.smsSkip.addEventListener('click', () => {
        if (!this.pending && this.step === 'sms') this.finishSignup();
      });
      this.trigger.addEventListener('click', () => {
        this.panel.hidden = !this.panel.hidden;
        this.trigger.setAttribute('aria-expanded', String(!this.panel.hidden));
        if (!this.panel.hidden) {
          if (this.step === 'sms') this.smsForm.elements.phone.focus();
          else if (this.step === 'email') this.form.elements.email.focus();
          else this.status.focus();
        }
      });
      this.form.addEventListener('submit', (event) => this.subscribe(event));
      this.updateVariant(JSON.parse(this.dataset.variant || 'null'));
    }

    disconnectedCallback() {
      this.buyButtonObserver?.disconnect();
    }

    observeBuyButton() {
      const root = this.closest('product-form');
      const button = root?.querySelector('.product-form__submit');
      const container = button?.parentElement;
      if (!button || !container) return;
      this.buyButtonObserver?.disconnect();
      const sync = () => {
        const width = button.offsetWidth;
        if (!width) return;
        const left = container.getBoundingClientRect().left - root.getBoundingClientRect().left
          + (container.clientWidth - width) / 2;
        this.style.setProperty('--omnee-restock-width', `${width}px`);
        this.style.setProperty('--omnee-restock-left', `${Math.max(0, left)}px`);
      };
      this.buyButtonObserver = new ResizeObserver(sync);
      [button, container, root].forEach(node => this.buyButtonObserver.observe(node));
      sync();
    }

    setupPhoneCountries() {
      if (!this.phoneCountry || typeof libphonenumber === 'undefined') return;
      const countries = this.smsCountries();
      this.phoneCountry.replaceChildren(...countries.map(country => {
        const option = document.createElement('option');
        option.value = country;
        option.textContent = `${country} +${libphonenumber.getCountryCallingCode(country)}`;
        return option;
      }));
      const detected = (this.dataset.country || '').toUpperCase();
      if (countries.includes(detected)) {
        this.phoneCountry.value = detected;
        [...this.phoneCountry.options].forEach(option => { option.defaultSelected = option.value === detected; });
      }
      this.phoneCountry.addEventListener('change', () => this.smsForm.elements.phone.setCustomValidity(''));
    }

    smsCountries() {
      if (typeof libphonenumber === 'undefined') return [];
      return [...new Set((this.dataset.smsCountries || '').toUpperCase().split(/[\s,]+/))]
        .filter(country => libphonenumber.isSupportedCountry(country));
    }

    normalizedPhone() {
      if (typeof libphonenumber === 'undefined') throw new Error('Phone validation is temporarily unavailable. Please retry later.');
      const countries = this.smsCountries();
      const selected = this.phoneCountry?.value;
      if (!countries.includes(selected)) throw new Error('Choose a supported phone country.');
      const value = this.smsForm.elements.phone.value.trim();
      let parsed;
      try {
        parsed = libphonenumber.parsePhoneNumberFromString(value, { defaultCountry: selected, extract: false });
      } catch {}
      if (!parsed || !parsed.isValid() || parsed.ext) throw new Error('Enter a valid phone number for the selected country.');
      if (!parsed.country || !countries.includes(parsed.country)) throw new Error('Text signup is not available for this phone number’s country.');
      this.phoneCountry.value = parsed.country;
      return parsed.number;
    }

    resetSteps() {
      this.flowVersion++;
      this.emailSignup = null;
      this.step = 'email';
      this.form.hidden = false;
      this.smsForm.hidden = true;
      this.smsForm.reset();
      this.smsForm.elements.phone.setCustomValidity('');
      if (this.support) this.support.hidden = false;
    }

    suspend() {
      if (this.initialized) this.resetSteps();
      this.hidden = true;
      this.variant = null;
      if (this.panel) this.panel.hidden = true;
      this.trigger?.setAttribute('aria-expanded', 'false');
    }

    updateVariant(variant) {
      if (!this.initialized) return;
      const changed = this.variant?.id !== variant?.id;
      this.variant = variant;
      this.hidden = !variant?.id || variant.available !== false;
      if (changed || this.hidden) {
        this.resetSteps();
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.status.textContent = '';
        this.form.hidden = false;
      }
      this.querySelector('[data-restock-selection]').textContent = variant
        ? `${this.dataset.productTitle}${variant.title === 'Default Title' ? '' : ` — ${variant.title}`}`
        : '';
    }

    async subscribe(event) {
      event.preventDefault();
      if (this.pending || this.step !== 'email' || !this.form.reportValidity()) return;
      const variant = this.variant;
      const selectedId = this.closest('product-form')?.querySelector('form [name="id"]')?.value;
      if (!variant?.id || variant.available !== false || String(variant.id) !== selectedId) {
        this.status.textContent = 'Please select an out-of-stock size or color first.';
        return;
      }
      const siteId = this.dataset.siteId.trim();
      if (!/^[a-zA-Z0-9]{6}$/.test(siteId)) {
        this.status.textContent = 'Restock alerts are temporarily unavailable. Please try again later.';
        return;
      }
      this.pending = true;
      this.submit.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = 'saving your signup…';
      const email = this.form.elements.email.value.trim();
      const flowVersion = this.flowVersion;
      const postSubscription = async (endpoint, data) => {
        const response = await fetch(`https://a.klaviyo.com/client/${endpoint}/?company_id=${encodeURIComponent(siteId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
          body: JSON.stringify({ data }),
          signal: AbortSignal.timeout(15000),
        });
        if (response.status !== 202) throw new Error('Subscription request was not accepted');
      };
      try {
        const results = await Promise.allSettled([
          postSubscription('back-in-stock-subscriptions', {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL'],
            },
            relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant.id}` } } },
          }),
          postSubscription('subscriptions', {
            type: 'subscription',
            attributes: {
              custom_source: 'Omnee restock signup',
              profile: { data: { type: 'profile', attributes: {
                email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              } } },
            },
            relationships: { list: { data: { type: 'list', id: 'Yq6uZq' } } },
          }),
        ]);
        if (this.flowVersion !== flowVersion || this.variant?.id !== variant.id) return;
        const restockSaved = results[0].status === 'fulfilled';
        const marketingSaved = results[1].status === 'fulfilled';
        if (restockSaved && marketingSaved) {
          this.emailSignup = { email, variant: { ...variant }, siteId };
          this.form.hidden = true;
          if (this.support) this.support.hidden = true;
          if (this.isSmsEligible()) {
            this.step = 'sms';
            this.smsForm.hidden = false;
            this.status.textContent = '';
            this.smsForm.elements.phone.focus();
          } else {
            this.finishSignup();
          }
        } else {
          this.form.hidden = false;
          this.status.textContent = restockSaved
            ? 'Your restock alert is saved, but we couldn’t save your omnee updates signup. Please submit again to retry.'
            : marketingSaved
              ? 'Your omnee updates request was accepted, but we couldn’t save your restock alert. Please submit again to retry.'
              : 'We couldn’t save your signup. Please submit again to retry.';
        }
      } finally {
        this.pending = false;
        this.submit.disabled = false;
        this.form.removeAttribute('aria-busy');
      }
    }
    isSmsEligible() {
      return this.isSmsCountryEligible();
    }

    isSmsCountryEligible() {
      const country = (this.dataset.country || '').trim().toUpperCase();
      const countries = this.smsCountries();
      return Boolean(country && countries.includes(country) && this.dataset.smsPoliciesReady === 'true');
    }

    finishSignup() {
      if (!this.emailSignup) return;
      this.step = 'success';
      this.form.hidden = true;
      this.smsForm.hidden = true;
      const variant = this.emailSignup.variant;
      this.status.textContent = `restock request recorded for ${this.dataset.productTitle}${variant.title === 'Default Title' ? '' : ` — ${variant.title}`}. check your inbox for any confirmation messages.`;
      this.status.focus();
    }

    async subscribeSms(event) {
      event.preventDefault();
      if (this.pending || this.step !== 'sms' || !this.emailSignup) return;
      if (!this.isSmsEligible()) {
        this.finishSignup();
        return;
      }
      const phoneInput = this.smsForm.elements.phone;
      let phone;
      phoneInput.setCustomValidity('');
      try {
        phone = this.normalizedPhone();
      } catch (error) {
        phoneInput.setCustomValidity(error.message);
      }
      if (!this.smsForm.reportValidity() || !phone) return;
      const signup = this.emailSignup;
      const flowVersion = this.flowVersion;
      const selectedId = this.closest('product-form')?.querySelector('form [name="id"]')?.value;
      if (String(signup.variant.id) !== selectedId || this.variant?.id !== signup.variant.id) {
        this.status.textContent = 'Your selection changed. Please start again for the new size or color.';
        return;
      }
      this.pending = true;
      this.smsSubmit.disabled = true;
      this.smsSkip.disabled = true;
      phoneInput.disabled = true;
      this.phoneCountry.disabled = true;
      this.smsForm.setAttribute('aria-busy', 'true');
      this.status.textContent = 'saving your text signup…';
      const profile = { email: signup.email, phone_number: phone };
      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(signup.siteId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
          body: JSON.stringify({ data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Omnee restock SMS signup',
              profile: { data: { type: 'profile', attributes: {
                ...profile,
                subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
              } } },
            },
            relationships: { list: { data: { type: 'list', id: 'SdGhbM' } } },
          } }),
          signal: AbortSignal.timeout(15000),
        });
        if (response.status !== 202) throw new Error('SMS marketing request was not accepted');
        if (this.flowVersion !== flowVersion || this.emailSignup !== signup) return;
        this.finishSignup();
      } catch {
        if (this.flowVersion !== flowVersion || this.emailSignup !== signup) return;
        this.status.textContent = 'We couldn’t save your text signup. Retry or skip. Your email alert is saved.';
      } finally {
        this.pending = false;
        this.smsSubmit.disabled = false;
        this.smsSkip.disabled = false;
        phoneInput.disabled = false;
        this.phoneCountry.disabled = false;
        this.smsForm.removeAttribute('aria-busy');
      }
    }
  });
}
